<script setup lang="ts">
import { computed } from 'vue';
import { getISOWeek, parseISO } from 'date-fns';
import { X } from 'lucide-vue-next';
import type { ColumnDef } from '@tanstack/vue-table';
import ActionModal from '@/components/common/actionModal/ActionModal.vue';
import MainViewCard from '@/components/common/MainViewCard.vue';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ColumnVisibilityMenu, DataTable, DataTableFacetFilter } from '@/components/ui/data-table';
import { SearchInput } from '@/components/ui/input';
import Spinner from '@/components/ui/Spinner.vue';
import { formatQuantity, PERCENT_TONE_CLASSES, signedNumber, signTone } from '@/composables/inventory/inventoryTable';
import { drawnFromLines, type DrawnFromLine } from '@/composables/inventory/drawnFrom';
import { movementDetailsText } from '@/composables/inventory/movementDetails';
import { useInventoryFormModal } from '@/composables/inventory/useInventoryFormModal';
import { useInventoryScope } from '@/composables/inventory/useInventoryScope';
import { useColumnVisibilityOverrides } from '@/composables/useColumnVisibilityOverrides';
import { i18n } from '@/plugins/internationalization/i18n';
import type { MedicineMovement } from '@/types';
import DiagnosisTotals from './DiagnosisTotals.vue';
import InventoryActions from './InventoryActions.vue';
import TreatmentsChart from './log/TreatmentsChart.vue';
import { movementTypeLabel, PAGE_SIZE_OPTIONS, useMovementsPage, type MovementsPageKey } from './useMovementsPage';

const props = withDefaults(
    defineProps<{
        /** The ledger key a detail page is fixed to. Absent on the Log page, which spans every selected farm. */
        ledgerKey?: MovementsPageKey | null;
    }>(),
    { ledgerKey: null }
);

const isDetail = computed(() => props.ledgerKey !== null);
const {
    diagnosisFilter,
    diagnosisOptions,
    diagnosisTotals,
    drugFilter,
    drugOptions,
    farmFilter,
    farmOptions,
    isError,
    isFetching,
    isTreatmentWeeksError,
    page,
    pageSize,
    refetch,
    resetWeek,
    rows,
    search,
    selectWeek,
    selectedWeekStart,
    sorting,
    totalPages,
    totalRows,
    netMovement,
    treatmentWeeks,
    treatmentWeeksKey,
    typeOptions,
    types,
} = useMovementsPage(computed(() => props.ledgerKey));
const { initialState, stepConfig, header, loadingKey, loadingRecordId, openMovement } = useInventoryFormModal();

type MovementColumnId = 'farm' | 'drug' | 'diagnosis' | 'details' | 'drawn_from' | 'balance_after' | 'user';

const TOGGLE_COLUMN_IDS: readonly MovementColumnId[] = ['farm', 'drug', 'diagnosis', 'details', 'drawn_from', 'balance_after', 'user'];

const heading = (key: string) => i18n.global.t(`views.health.inventory.table.headings.${key}`);

/** The columns whose heading key differs from their id. */
const COLUMN_HEADING_KEYS: Partial<Record<MovementColumnId, string>> = { balance_after: 'balanceAfter', drawn_from: 'drawnFrom' };

/** A detail page is already one farm, drug and unit, so those columns would only repeat the page header; the user column is off on the Log, to fit the rest. */
const { isVisible, toggleColumn, setAllColumns, columnToggleOptions } = useColumnVisibilityOverrides<MovementColumnId>({
    storageKey: 'health.inventory.log.columnVisibility',
    columnIds: TOGGLE_COLUMN_IDS,
    // Which lot a movement drew from is worked out per key, so only a single key's page has it.
    isAvailable: (id) => (isDetail.value ? id !== 'farm' && id !== 'drug' : id !== 'drawn_from'),
    defaultVisible: (id) => {
        if (id === 'user') {
            return isDetail.value;
        }

        // The key's diagnosis would only repeat on every row of a detail page.
        return id !== 'diagnosis' || !isDetail.value;
    },
    label: (id) => heading(COLUMN_HEADING_KEYS[id] ?? id),
    // A hidden column has no header left to clear its filter from, so hiding it drops the filter.
    onHide: (id) => {
        if (id === 'farm') {
            farmFilter.value = [];
        } else if (id === 'drug') {
            drugFilter.value = [];
        } else if (id === 'diagnosis') {
            diagnosisFilter.value = [];
        }
    },
});

const columns = computed<ColumnDef<MedicineMovement>[]>(() => {
    const defs: ColumnDef<MedicineMovement>[] = [
        { accessorKey: 'date', header: heading('date'), enableSorting: true },
        { accessorKey: 'type', header: heading('type') },
    ];

    if (isVisible('farm')) {
        defs.push({ accessorKey: 'farm_name', header: heading('farm') });
    }
    if (isVisible('drug')) {
        defs.push({ accessorKey: 'drug_name', header: heading('drug') });
    }
    if (isVisible('diagnosis')) {
        defs.push({ accessorKey: 'diagnosis', header: heading('diagnosis') });
    }
    if (isVisible('details')) {
        defs.push({ accessorKey: 'details', header: heading('details') });
    }

    if (isVisible('drawn_from')) {
        defs.push({ accessorKey: 'drawn_from', header: heading('drawnFrom') });
    }

    defs.push({ accessorKey: 'quantity', header: heading('quantity'), enableSorting: true, meta: { align: 'right' } });

    if (isVisible('balance_after')) {
        defs.push({ accessorKey: 'balance_after', header: heading('balanceAfter'), meta: { align: 'right' } });
    }
    if (isVisible('user')) {
        defs.push({ accessorKey: 'user_name', header: heading('user'), enableSorting: true });
    }

    return defs;
});

/** The week picked on the chart, as the table header names it. */
const selectedWeekLabel = computed(() => (selectedWeekStart.value === null ? null : i18n.global.t('views.health.inventory.chart.weekFilter', { week: getISOWeek(parseISO(selectedWeekStart.value)) })));

function formatDate(date: string): string {
    return i18n.global.d(parseISO(date), 'short');
}

function detailsOf(movement: MedicineMovement): string {
    return movementDetailsText(movement, { t: (key) => i18n.global.t(key), n: formatQuantity });
}

const TREATMENT_BADGE_CLASSES = 'border-transparent bg-treatment/10 text-treatment';

/** Purchases read green, spillage red, counts amber and treatments in the treatment colour. */
const MOVEMENT_TYPE_CLASSES: Partial<Record<MedicineMovement['type'], string>> = {
    purchase: PERCENT_TONE_CLASSES.ok,
    treatment: TREATMENT_BADGE_CLASSES,
    retreatment: TREATMENT_BADGE_CLASSES,
    waste: PERCENT_TONE_CLASSES.error,
    count: PERCENT_TONE_CLASSES.warning,
};

const DRAWN_FROM_TONE_CLASSES: Record<DrawnFromLine['tone'], string> = { normal: '', uncovered: 'text-red-600', note: 'text-gray-500' };

function drawnFromOf(movement: MedicineMovement): DrawnFromLine[] {
    return drawnFromLines(movement, { t: (key, params) => i18n.global.t(key, params ?? {}), n: formatQuantity, date: (day) => formatDate(day) });
}

function badgeVariantOf(type: MedicineMovement['type']) {
    return MOVEMENT_TYPE_CLASSES[type] ? 'outline' : 'secondary';
}

/** The sign of a quantity gives its colour: a deduction red, an addition green; a count that found nothing missing stays neutral. */
function quantityTextOf(movement: MedicineMovement): string {
    return movement.type === 'count' && movement.quantity === 0 ? '±0' : signedNumber(movement.quantity, formatQuantity);
}

function isLoading(movement: MedicineMovement): boolean {
    return loadingKey.value === movement.key || (movement.record_id !== null && loadingRecordId.value === movement.record_id);
}

const isAnyLoading = computed(() => loadingKey.value !== null || loadingRecordId.value !== null);

function rowIdOf(movement: MedicineMovement): string {
    return movement.key;
}

/**
 * A detail page lists the key's whole history, while the figures above cover only the period: rows
 * outside it are muted, and while the list is in date order a labelled divider marks where it
 * crosses into or out of the period.
 */
const { periodRange } = useInventoryScope();

type PeriodZone = 'before' | 'in' | 'after';

function zoneOf(movement: MedicineMovement): PeriodZone {
    const { from, to } = periodRange.value;
    const day = movement.date.slice(0, 10);

    if (from !== '' && day < from) {
        return 'before';
    }

    return to !== '' && day > to ? 'after' : 'in';
}

const PERIOD_ZONE_LABEL_KEYS: Record<PeriodZone, string> = {
    before: 'views.health.inventory.detail.zones.before',
    in: 'views.health.inventory.detail.zones.in',
    after: 'views.health.inventory.detail.zones.after',
};

/** A divider above the first row of each zone; none at the top of a page that starts inside the period, and none unless sorted by date. */
function periodDividerOf(movement: MedicineMovement, index: number): string | null {
    if (!isDetail.value || (sorting.value[0]?.id ?? 'date') !== 'date') {
        return null;
    }

    const zone = zoneOf(movement);
    const previous = index > 0 ? rows.value[index - 1] : undefined;
    const startsZone = previous ? zoneOf(previous) !== zone : zone !== 'in';

    return startsZone ? i18n.global.t(PERIOD_ZONE_LABEL_KEYS[zone]) : null;
}

/** Every row opens its movement, so every row reads as clickable. */
function rowClassOf(movement: MedicineMovement): string {
    return isDetail.value && zoneOf(movement) !== 'in' ? 'cursor-pointer opacity-60' : 'cursor-pointer';
}
</script>

<template>
    <div class="space-y-8">
        <MainViewCard v-if="!isDetail && !isTreatmentWeeksError" class="py-4 px-2">
            <TreatmentsChart :weeks="treatmentWeeks" :scope-key="treatmentWeeksKey" :selected-week-start="selectedWeekStart" @week-selected="selectWeek" @selection-reset="resetWeek" />
        </MainViewCard>
        <MainViewCard class="overflow-hidden">
            <div v-if="isError" class="py-14 text-center px-4 text-gray-500 text-lg space-y-4">
                <div>{{ $t('views.pigs.records.errors.couldNotLoadData') }}</div>
                <Button variant="outline" @click="refetch()">{{ $t('views.pigs.records.errors.retry') }}</Button>
            </div>
            <div v-else>
                <div class="px-4 pt-4 pb-2 flex flex-wrap items-center justify-between gap-4">
                    <h2 class="text-lg font-medium">{{ $t(isDetail ? 'views.health.inventory.detail.historyHeading' : 'views.health.inventory.movements.heading') }}</h2>
                    <div class="flex flex-wrap items-center gap-2">
                        <!-- The week picked on the chart narrows the rows; the chip says so and clears it. -->
                        <Badge v-if="selectedWeekLabel" variant="outline" class="h-9 gap-x-1 rounded-lg text-normal font-normal">
                            {{ selectedWeekLabel }}
                            <button type="button" class="rounded hover:bg-gray-100" :aria-label="$t('views.health.inventory.chart.clearWeek')" @click="resetWeek">
                                <X class="size-4" aria-hidden="true" />
                            </button>
                        </Badge>
                        <SearchInput
                            v-model="search"
                            class="w-64"
                            :placeholder="$t(isDetail ? 'views.health.inventory.movements.searchPlaceholderDetail' : 'views.health.inventory.movements.searchPlaceholder')"
                        />
                        <!-- Purchases, counts and waste are registered from the stock list and a drug's own page, not the Log. -->
                        <InventoryActions v-if="isDetail" :prefill="ledgerKey ?? undefined" />
                        <ColumnVisibilityMenu
                            v-if="columnToggleOptions.length"
                            icon-only
                            :options="columnToggleOptions"
                            :label="$t('views.health.inventory.table.columnsButton')"
                            @toggle="toggleColumn"
                            @select-all="setAllColumns(true)"
                            @deselect-all="setAllColumns(false)"
                        />
                    </div>
                </div>
                <DiagnosisTotals v-if="diagnosisTotals" :totals="diagnosisTotals" class="px-4 pb-3" />
                <!-- A save or delete reloads the inventory queries through `useClearCachesOnFormSubmission`, so the modal needs no close handler here. -->
                <ActionModal :initial-step-index="1" :initial-state="initialState" :step-config="stepConfig" :header="header">
                    <template #default="{ open }">
                        <DataTable
                            v-model:sorting="sorting"
                            v-model:page="page"
                            v-model:page-size="pageSize"
                            :data="rows"
                            :columns="columns"
                            :loading="isFetching"
                            :page-size-options="PAGE_SIZE_OPTIONS"
                            :total-pages="totalPages"
                            :total-rows="totalRows"
                            :row-id="rowIdOf"
                            :row-class="rowClassOf"
                            :row-divider="periodDividerOf"
                            server-side
                            row-clickable
                            @row-click="openMovement($event, open)"
                        >
                            <!-- The Log's totals row, as on the records tables: how many rows the filters leave, and their net
                                 movement when they are one drug in one unit (units do not add up otherwise). -->
                            <template v-if="!isDetail" #totals[date]>
                                <span class="whitespace-nowrap">{{ $t('views.health.inventory.movements.totalRows', { count: $n(totalRows, 'decimal') }) }}</span>
                            </template>
                            <template v-if="!isDetail" #totals[quantity]>
                                <span v-if="netMovement" class="whitespace-nowrap" :class="{ 'text-red-600': netMovement.quantity < 0 }">
                                    {{ signedNumber(netMovement.quantity, formatQuantity) }} {{ netMovement.unitName }}
                                </span>
                                <span v-else class="text-gray-400" aria-hidden="true">&ndash;</span>
                            </template>
                            <template #header[type]>
                                <div class="flex items-center gap-x-0.5">
                                    <span>{{ heading('type') }}</span>
                                    <DataTableFacetFilter :title="heading('type')" :options="typeOptions" :model-value="types" @update:model-value="types = $event.map(String)" />
                                </div>
                            </template>
                            <template #header[farm_name]>
                                <div class="flex items-center gap-x-0.5">
                                    <span>{{ heading('farm') }}</span>
                                    <DataTableFacetFilter :title="heading('farm')" :options="farmOptions" :model-value="farmFilter" @update:model-value="farmFilter = $event.map(String)" />
                                </div>
                            </template>
                            <template #header[drug_name]>
                                <div class="flex items-center gap-x-0.5">
                                    <span>{{ heading('drug') }}</span>
                                    <DataTableFacetFilter :title="heading('drug')" :options="drugOptions" :model-value="drugFilter" @update:model-value="drugFilter = $event.map(String)" />
                                </div>
                            </template>
                            <template #header[diagnosis]>
                                <div class="flex items-center gap-x-0.5">
                                    <span>{{ heading('diagnosis') }}</span>
                                    <DataTableFacetFilter :title="heading('diagnosis')" :options="diagnosisOptions" :model-value="diagnosisFilter" @update:model-value="diagnosisFilter = $event.map(String)" />
                                </div>
                            </template>
                            <template #item[date]="{ item }">
                                <!-- The button is what keyboards and screen readers open; a click anywhere else on the row does the same. -->
                                <button
                                    type="button"
                                    class="whitespace-nowrap rounded-sm hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-700"
                                    @click.stop="openMovement(item, open)"
                                >
                                    {{ formatDate(item.date) }}
                                </button>
                                <Spinner v-if="isLoading(item)" class="ml-2 inline h-4 w-4 align-middle" aria-hidden="true" />
                            </template>
                            <template #item[type]="{ item }">
                                <Badge :variant="badgeVariantOf(item.type)" :class="MOVEMENT_TYPE_CLASSES[item.type]">{{ movementTypeLabel(item.type) }}</Badge>
                            </template>
                            <template #item[farm_name]="{ item }">{{ item.farm_name }}</template>
                            <template #item[drug_name]="{ item }">
                                <div class="min-w-36">
                                    <span class="font-medium">{{ item.drug_name }}</span>
                                    <span class="text-gray-500"> ({{ item.unit_name }})</span>
                                </div>
                            </template>
                            <template #item[diagnosis]="{ item }">{{ item.diagnosis ?? '–' }}</template>
                            <template #item[details]="{ item }">
                                <div class="min-w-40">{{ detailsOf(item) }}</div>
                            </template>
                            <template #item[drawn_from]="{ item }">
                                <div class="min-w-48 space-y-0.5">
                                    <div v-for="line in drawnFromOf(item)" :key="line.text" :class="DRAWN_FROM_TONE_CLASSES[line.tone]">{{ line.text }}</div>
                                    <span v-if="drawnFromOf(item).length === 0" class="text-gray-400" aria-hidden="true">&ndash;</span>
                                </div>
                            </template>
                            <template #item[quantity]="{ item }">
                                <span class="whitespace-nowrap" :class="signTone(item.quantity)">{{ quantityTextOf(item) }} {{ item.unit_name }}</span>
                            </template>
                            <template #item[balance_after]="{ item }">
                                <span class="whitespace-nowrap" :class="{ 'text-red-600': item.balance_after < 0 }">{{ formatQuantity(item.balance_after) }} {{ item.unit_name }}</span>
                            </template>
                            <template #item[user_name]="{ item }">{{ item.user_name ?? '—' }}</template>
                        </DataTable>
                    </template>
                </ActionModal>
            </div>
        </MainViewCard>
    </div>
</template>
